/* GASSISA
 =========================================================================================
 * Descripcion: Perfil fisico de la impresora termica. Es la unica fuente de verdad
 *              de las unidades (dots), de las conversiones a centimetros/milimetros
 *              y de los limites reales del transport (PrinterTools).
 *
 *              dots: 1 dot = 1/203 pulgada = 0.125 mm  (impresora termica 203 dpi)
 *              => 8 dots por mm, 80 dots por cm.
 *
 *              Limites impuestos por PrinterTools (/api/imprimirticket):
 *                - decodifica el binario como utf-8 estricto: todo byte de comando
 *                  debe ser <= 0x7F, por eso MAX_BYTE_SEGURO.
 *                - acepta hasta 96 caracteres por linea; el ajuste final al ancho
 *                  fisico (32/42/60 columnas segun la impresora) lo hace el
 *                  firmware al imprimir.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 20/10/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion (extraccion de ticketVentaEscape)
 * ---------------------------------------------------------------------------------------*/

export const DOTS_POR_MM = 8;            // 203 dpi (203 / 25.4 = 7.99)
export const DOTS_POR_CM = DOTS_POR_MM * 10;
export const MM_PX = 3.78;               // 96 dpi en pantalla (96 / 25.4)
export const PX_POR_DOT = MM_PX / DOTS_POR_MM;

export const DOTS_MAX_IMPRESION = 384;   // 48 mm imprimibles = 32 columnas de Font A
export const MAX_COLUMNAS_LINEA = 96;    // capacidad de transporte de PrinterTools (line[:96])
export const MAX_LOGO_WIDTH_DOTS = 384;  // max_width fijo de PrinterTools (384 px)

// Columnas que caben en un ancho dado (sin tope): 80 mm -> 72 mm -> 60 columnas.
export const calcularColumnasAncho = (anchoDots, fuenteTipo = "A", mult = 1) => {
    const columnas = Math.floor((Number(anchoDots) || 0) / (anchoFuenteDots(fuenteTipo) * mult));
    return Math.max(1, columnas);
};

export const FONT_A_WIDTH_DOTS = 12;     // Font A 12x24 dots
export const FONT_A_HEIGHT_DOTS = 24;
export const FONT_B_WIDTH_DOTS = 9;      // Font B 9x17 dots
export const FONT_B_HEIGHT_DOTS = 17;

export const MAX_BYTE_SEGURO = 0x7F;     // PrinterTools decodifica en utf-8 estricto
export const MAX_AVANCE_DOTS = 127;      // ESC 3 n admite n <= 127 por comando

export const AGRUPACION_Y_MM = 0.5;      // tolerancia para agrupar objetos en un renglon

export const SECCION_DEFAULT = {
    ancho: 85,
    alto: 50,
    negrita: false,
    alineacion: "center",
    fuenteTipo: "A",
};

export const cmToDots = (cm) => Math.round((Number(cm) || 0) * DOTS_POR_CM);
export const dotsToCm = (dots) => (Number(dots) || 0) / DOTS_POR_CM;

// Tipos de fuente ESC/POS:
//   A = Font A (12x24 dots)
//   B = Font B (9x17 dots)
//   D = Font A ampliada 2x (24x48 dots), para titulos o totales
// El multiplicador se deriva del TIPO, no de un tamano en puntos.
export const normalizarFuenteTipo = (fuenteTipo) => (
    fuenteTipo === "B" ? "B" : fuenteTipo === "D" ? "D" : "A"
);

export const multiplicadorFuenteTipo = (fuenteTipo) => (
    normalizarFuenteTipo(fuenteTipo) === "D" ? 2 : 1
);

export const anchoFuenteDots = (fuenteTipo) => (
    normalizarFuenteTipo(fuenteTipo) === "B" ? FONT_B_WIDTH_DOTS : FONT_A_WIDTH_DOTS
);
export const altoFuenteDots = (fuenteTipo) => (
    normalizarFuenteTipo(fuenteTipo) === "B" ? FONT_B_HEIGHT_DOTS : FONT_A_HEIGHT_DOTS
);

export const calcularColumnas = (anchoMm, fuenteTipo = "A", mult = 1, columnasMax = MAX_COLUMNAS_LINEA) => {
    const anchoDots = (Number(anchoMm) || 0) * DOTS_POR_MM;
    const columnas = Math.floor(anchoDots / (anchoFuenteDots(fuenteTipo) * mult));
    return Math.max(8, Math.min(columnas, columnasMax));
};

// Columnas disponibles sobre un ancho ya expresado en dots.
export const calcularColumnasDots = (anchoDots, fuenteTipo = "A", mult = 1, columnasMax = MAX_COLUMNAS_LINEA) => {
    const columnas = Math.floor((Number(anchoDots) || 0) / (anchoFuenteDots(fuenteTipo) * mult));
    return Math.max(1, Math.min(columnas, columnasMax));
};

// Regla de corte de linea: parte por palabras respetando el ancho en columnas.
export const ajustarTextoAColumnas = (texto, columnas) => {
    const lineas = [];
    String(texto ?? "").split("\n").forEach((linea) => {
        let restante = linea.trimEnd();
        if (restante.length === 0) {
            lineas.push("");
            return;
        }
        while (restante.length > columnas) {
            let corte = restante.lastIndexOf(" ", columnas);
            if (corte <= 0) corte = columnas;
            lineas.push(restante.slice(0, corte).trimEnd());
            restante = restante.slice(corte).trimStart();
        }
        lineas.push(restante);
    });
    return lineas.join("\n");
};

// Longitud en bytes de los comandos de estilo de un objeto tomando el estado
// inicial de la impresora (ESC M / GS ! / ESC E). Debe coincidir con la
// longitud que emite comandosEstilo cuando el estado es el inicial.
export const longitudEstiloObjeto = (objeto) => {
    const tipo = normalizarFuenteTipo(objeto?.fuenteTipo);
    const mult = multiplicadorFuenteTipo(tipo);
    let bytes = 0;
    if (tipo === "B") bytes += 2;   // ESC M n
    if (mult !== 1) bytes += 4;     // GS ! nL nH
    if (objeto?.negrita) bytes += 2; // ESC E n
    return bytes;
};

// Un separador (----, ...., - - -) no se parte por palabras.
export const esSeparadorRepetido = (texto) => /^([^\w\s])\1+$/.test(String(texto ?? ""));

// Metrica de la impresora proyectada a pantalla (96 dpi):
// el ancho real de un caracter ESC/POS es 12 dots (Font A) o 9 dots (Font B),
// escalado por el multiplicador. Courier New tiene un avance de 0.6 em, asi que
// el tamano de fuente que reproduce ese ancho es charPx / 0.6.
// Con esto la vista previa en pantalla respeta el ancho de columna indicado
// (por defecto, la referencia de 32 columnas de 58 mm).
export const anchoCaracterPx = (fuenteTipo, mult = 1) => anchoFuenteDots(fuenteTipo) * mult * PX_POR_DOT;
export const tamanoFuentePreview = (fuenteTipo, mult = 1) => anchoCaracterPx(fuenteTipo, mult) / 0.6;
// Igual, pero con el ancho real del caracter de la impresora (9 o 12 dots).
export const tamanoFuentePreviewCaracter = (anchoCaracterDots, mult = 1) => (anchoCaracterDots * mult * PX_POR_DOT) / 0.6;
export const altoLineaPreviewPx = (fuenteTipo, mult = 1) => altoFuenteDots(fuenteTipo) * mult * PX_POR_DOT;
export const anchoPapelPx = (anchoDots) => (Number(anchoDots) || 0) * PX_POR_DOT;

// ---------------------------------------------------------------------------
// CALIBRACION DE LA IMPRESORA
//
// El diseno trabaja en milimetros, pero la impresora trabaja en DOTS
// (horizontales) y en RENGLONES (verticales). La conversion depende de la
// impresora real, que se mide con el gage test:
//
//   dotsPorMm     = DPI real / 25.4   (203 dpi -> 8.00 ; 180 dpi -> 7.09)
//   unidadesLinea = ESC 3 n (n/180 de pulgada). Con Font A el alto del caracter
//                   son 24 unidades = 3.3867 mm: es el paso vertical minimo.
//
// ESC/POS NO tiene cursor Y absoluto: la vertical solo avanza emitiendo lineas
// (ESC 3 n + salto) y la impresora topa el interlineado a la altura del
// caracter. Por eso la vertical del diseno se expresa en RENGLONES.
// ---------------------------------------------------------------------------
export const CALIBRACION_POR_DEFECTO = {
    dotsPorMm: 8,          // 203 dpi
    unidadesLinea: 24,     // ESC 3 n -> 24/180" = 3.3867 mm (Font A)
    // Ancho real del caracter de la impresora. Las termicas de 58/80 mm suelen
    // imprimir con 9 puntos (Font B): 32 columnas x 9 dots = 36 mm de area
    // imprimible. Con 12 puntos serian 48 mm. Se mide con el gage test.
    anchoCaracterDots: 9,
};

export const anchoCaracterDotsDe = (calibracion = CALIBRACION_POR_DEFECTO) => (
    Number(calibracion?.anchoCaracterDots ?? CALIBRACION_POR_DEFECTO.anchoCaracterDots) || 9
);

// Ancho util (area imprimible) en milimetros: columnas x ancho del caracter.
export const anchoUtilMm = (columnas, calibracion = CALIBRACION_POR_DEFECTO) => {
    const dots = (Number(columnas) || 0) * anchoCaracterDotsDe(calibracion);
    return dots / dotsPorMmDe(calibracion);
};

// Ancho de un caracter en milimetros.
export const anchoCaracterMm = (calibracion = CALIBRACION_POR_DEFECTO) => (
    anchoCaracterDotsDe(calibracion) / dotsPorMmDe(calibracion)
);

export const altoLineaMm = (calibracion = CALIBRACION_POR_DEFECTO) => {
    const unidades = Number(calibracion?.unidadesLinea ?? CALIBRACION_POR_DEFECTO.unidadesLinea) || 24;
    return (unidades / 180) * 25.4;
};

export const dotsPorMmDe = (calibracion = CALIBRACION_POR_DEFECTO) => (
    Number(calibracion?.dotsPorMm ?? CALIBRACION_POR_DEFECTO.dotsPorMm) || 8
);

export const mmToDots = (mm, calibracion = CALIBRACION_POR_DEFECTO) => Math.round(
    (Number(mm) || 0) * dotsPorMmDe(calibracion)
);

export const dotsToMm = (dots, calibracion = CALIBRACION_POR_DEFECTO) => (
    (Number(dots) || 0) / dotsPorMmDe(calibracion)
);

// Milimetros -> renglones (la unidad vertical real). Minimo 1 renglon.
export const mmToLineas = (mm, calibracion = CALIBRACION_POR_DEFECTO) => {
    const alto = altoLineaMm(calibracion);
    return Math.max(1, Math.round((Number(mm) || 0) / alto));
};

export const lineasToMm = (lineas, calibracion = CALIBRACION_POR_DEFECTO) => (
    (Number(lineas) || 0) * altoLineaMm(calibracion)
);

// Metricas de la vista previa para un layout real: el ancho sale de las
// columnas guardadas por el ancho real del caracter, no de 48 mm fijos.
export const metricasPreviewTicket = (columnas = 32, calibracion = CALIBRACION_POR_DEFECTO) => {
    const calibracionEfectiva = { ...CALIBRACION_POR_DEFECTO, ...(calibracion || {}) };
    const columnasEfectivas = Math.max(1, Math.round(Number(columnas) || 32));
    const anchoCaracterDots = anchoCaracterDotsDe(calibracionEfectiva);
    const anchoDots = columnasEfectivas * anchoCaracterDots;
    const fontSize = tamanoFuentePreviewCaracter(anchoCaracterDots, 1);
    return {
        columnas: columnasEfectivas,
        calibracion: calibracionEfectiva,
        anchoCaracterDots,
        anchoDots,
        anchoPx: anchoPapelPx(anchoDots),
        anchoMm: anchoDots / dotsPorMmDe(calibracionEfectiva),
        fontSize,
        lineHeight: Math.max(altoLineaMm(calibracionEfectiva) * MM_PX, fontSize * 1.2),
    };
};

